import type { ButtonProps as ChakraButtonProps } from '@chakra-ui/react';
import { AbsoluteCenter, Button as ChakraButton, Span } from '@chakra-ui/react';
import type { LucideIcon } from 'lucide-react';
import * as React from 'react';

import { Spinner } from '../spinner';
import type { ButtonSize } from './button.kds';
import { IconSlot } from './icon-slot';

interface ButtonLoadingProps {
  loading?: boolean;
  loadingText?: React.ReactNode;
}

export interface ButtonProps extends Omit<ChakraButtonProps, 'size'>, ButtonLoadingProps {
  size?: ButtonSize;
  /** Lucide icon component — e.g. `ChevronLeft` from `lucide-react`. */
  leftIcon?: LucideIcon;
  /** Lucide icon component — e.g. `ChevronRight` from `lucide-react`. */
  rightIcon?: LucideIcon;
}

interface ButtonContentProps {
  children: React.ReactNode;
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon;
  size: ButtonSize;
}

const ButtonContent = ({ children, leftIcon: LeftIcon, rightIcon: RightIcon, size }: Readonly<ButtonContentProps>) => (
  <>
    {LeftIcon ? <IconSlot icon={LeftIcon} size={size} /> : null}
    {children}
    {RightIcon ? <IconSlot icon={RightIcon} size={size} /> : null}
  </>
);

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  const { loading, disabled, loadingText, children, leftIcon, rightIcon, size = 'rg', colorPalette, variant, ...rest } =
    props;
  const effectiveColorPalette = colorPalette ?? (variant === 'ghost' ? 'neutral' : undefined);
  const content = (
    <ButtonContent leftIcon={leftIcon} rightIcon={rightIcon} size={size}>
      {children}
    </ButtonContent>
  );

  let body: React.ReactNode = content;
  if (loading && loadingText) {
    body = (
      <>
        <Spinner size="inherit" color="inherit" />
        {loadingText}
      </>
    );
  } else if (loading) {
    body = (
      <>
        <AbsoluteCenter display="inline-flex">
          <Spinner size="inherit" color="inherit" />
        </AbsoluteCenter>
        <Span opacity={0} aria-hidden>
          {content}
        </Span>
      </>
    );
  }

  return (
    <ChakraButton
      aria-busy={loading || undefined}
      disabled={loading || disabled}
      ref={ref}
      {...({ ...rest, size, variant, colorPalette: effectiveColorPalette } as ChakraButtonProps)}
    >
      {body}
    </ChakraButton>
  );
});
